import React, { useMemo, useState } from 'react';
import { v4 as UUID } from 'uuid';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import classNames from 'classnames';
import styles from './KeywordFormSection.module.scss';
import { validateKeyword } from './validateKeyword';
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Icon,
  IconList,
  List,
  ListItem,
  Text,
} from 'components/core';
import { Inline, Stack } from 'components/layout';

type APIError = {
  responseJSON: {
    errors: Record<'included_keywords' | 'excluded_keywords', string[]>;
  };
};

export type Keyword = { value: string };

type FormValues = {
  newKeyword: string;
  keywords: Keyword[];
};

type KeywordFormSectionProps = {
  title: string;
  titleColor: 'success' | 'danger';
  label: string;
  keywords: string[];
  onUpdate: (data: string[]) => Promise<any>;
  onApiError: (errors: string[] | null) => void;
};

const KeywordFormSection = ({
  title,
  titleColor,
  label,
  keywords,
  onUpdate,
  onApiError,
}: KeywordFormSectionProps) => {
  const describedById = useMemo(() => UUID(), []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingKeywords, setDeletingKeywords] = useState<string[]>([]);
  const [screenReaderNotification, setScreenReaderNotification] = useState('');

  const {
    register,
    control,
    resetField,
    handleSubmit,
    formState,
  } = useForm<FormValues>({
    defaultValues: {
      newKeyword: '',
      keywords: keywords.map((word) => ({ value: word })),
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'keywords',
  });

  const newKeywordError = formState.errors.newKeyword?.message;

  const handleKeywordUpdate = async ({
    updatedWords,
    onSuccess,
    onComplete = () => null,
    screenReaderNotification,
  }: {
    updatedWords: string[];
    onSuccess: () => void;
    onComplete: () => void;
    screenReaderNotification: string;
  }) => {
    setScreenReaderNotification('');
    try {
      onApiError(null);
      await onUpdate(updatedWords);
      onSuccess();
      setScreenReaderNotification(screenReaderNotification);
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        Object.keys(error).includes('responseJSON')
      ) {
        const apiError = error as APIError;
        const {
          included_keywords = [],
          excluded_keywords = [],
        } = apiError.responseJSON.errors;

        const errors = [...included_keywords, ...excluded_keywords];

        onApiError(errors);
      } else {
        onApiError(['Something went wrong']);
      }
    } finally {
      onComplete();
    }
  };

  const handleAddKeyword: SubmitHandler<FormValues> = async (data, event) => {
    event?.preventDefault();

    const newWord = data.newKeyword.trim();
    if (!newWord) return;

    const updatedWords = [...fields.map((f) => f.value), newWord];

    setIsSubmitting(true);

    await handleKeywordUpdate({
      updatedWords,
      onSuccess: () => {
        append({ value: newWord });
        resetField('newKeyword');
      },
      onComplete: () => setIsSubmitting(false),
      screenReaderNotification: `Added ${newWord}`,
    });
  };

  const handleDeleteKeyword = async (index: number) => {
    const wordToDelete = fields[index].value;

    setDeletingKeywords((prev) => [...prev, wordToDelete]);

    const updatedWords = fields
      .filter((_, i) => i !== index)
      .map((field) => field.value);

    await handleKeywordUpdate({
      updatedWords,
      onSuccess: () => remove(index),
      onComplete: () => {
        setDeletingKeywords((prev) =>
          prev.filter((word) => word !== wordToDelete)
        );
      },
      screenReaderNotification: `Removed ${wordToDelete}`,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleAddKeyword)} className={styles.form}>
      <Stack spacing={3} className={styles.formGroup} component="fieldset">
        <Text
          className={styles.formLegend}
          variant={titleColor}
          component="legend"
          size="sm"
        >
          {title}
        </Text>
        <Inline alignY="top" spacing={3}>
          <FormControl error={newKeywordError}>
            <FormLabel isVisuallyHidden>{label}</FormLabel>
            <Input
              size="sm"
              aria-describedby={describedById}
              {...register('newKeyword', {
                validate: (value) => validateKeyword(value, fields),
              })}
            />
            <Text
              size="xs"
              variant="weak"
              id={describedById}
              className={classNames({
                [styles.visuallyHidden]: Boolean(newKeywordError),
              })}
            >
              Add a word or phrase
            </Text>
          </FormControl>

          <Button
            isLoading={isSubmitting}
            htmlType="submit"
            type="brand"
            variant="outline"
            aria-label="Add"
            size="sm"
          >
            Add
          </Button>
        </Inline>

        <div aria-live="polite" role="status" className={styles.visuallyHidden}>
          {screenReaderNotification}
        </div>

        <Stack spacing={2} component={List} className={styles.highlightList}>
          {fields.map((field, index) => (
            <Inline
              alignX="left"
              key={field.id}
              spacing={3}
              component={ListItem}
            >
              <Text size="sm" className={styles.highlight} variant="neutral">
                {field.value}
              </Text>
              <Button
                iconOnly
                htmlType="button"
                type="neutral"
                variant="ghost"
                size="sm"
                isLoading={deletingKeywords.includes(field.value)}
                onClick={() => handleDeleteKeyword(index)}
                aria-label={`Delete ${
                  field.value
                } from ${title.toLowerCase()} words`}
              >
                <Icon size="md" icon={IconList.light.faTrashCan} />
              </Button>
            </Inline>
          ))}
        </Stack>
      </Stack>
    </form>
  );
};

export default KeywordFormSection;
